import React, { useState, useEffect } from 'react';
import { Download, FileText, FileJson, Filter, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { api } from '@/services/api';

type ExportFormat = 'csv' | 'json';
type ExportType = 'transactions' | 'receipts';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: ExportType;
}

const typeOptions = [
  { value: 'transactions', label: 'Transacoes' },
  { value: 'receipts', label: 'Recibos' },
];


const transactionTypeOptions = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'expense', label: 'Apenas despesas' },
  { value: 'income', label: 'Apenas receitas' },
];

function convertToCSV(data: any[], type: ExportType): string {
  if (data.length === 0) return '';

  if (type === 'transactions') {
    const headers = ['Data', 'Tipo', 'Categoria', 'Descricao', 'Comerciante', 'Valor', 'Moeda', 'Dedutivel'];
    const rows = data.map((t) => [
      new Date(t.createdAt).toLocaleDateString('pt-BR'),
      t.type === 'income' ? 'Receita' : 'Despesa',
      t.category?.name || t.category || '-',
      t.description || '-',
      t.merchantName || '-',
      t.amount,
      t.currency || 'BRL',
      t.taxDeductible ? 'Sim' : 'Nao',
    ]);

    return [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');
  }

  if (type === 'receipts') {
    const headers = ['Data', 'Status', 'Comerciante', 'CNPJ', 'Valor', 'Moeda', 'Dedutivel', 'Numero'];
    const rows = data.map((r) => [
      new Date(r.createdAt).toLocaleDateString('pt-BR'),
      r.status,
      r.merchantName || '-',
      r.merchantTaxId || '-',
      r.totalAmount || '-',
      r.currency || 'BRL',
      r.taxDeductible ? 'Sim' : 'Nao',
      r.receiptNumber || '-',
    ]);

    return [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');
  }

  return '';
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// All month labels
const ALL_MONTH_LABELS: Record<number, string> = {
  1: 'Janeiro',
  2: 'Fevereiro',
  3: 'Marco',
  4: 'Abril',
  5: 'Maio',
  6: 'Junho',
  7: 'Julho',
  8: 'Agosto',
  9: 'Setembro',
  10: 'Outubro',
  11: 'Novembro',
  12: 'Dezembro',
};

export const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, type: initialType }) => {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [exportType, setExportType] = useState<ExportType>(initialType || 'transactions');
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');
  const [transactionType, setTransactionType] = useState('all');
  const [taxDeductibleOnly, setTaxDeductibleOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableYears, setAvailableYears] = useState<{ value: string; label: string }[]>([
    { value: 'all', label: 'Todos os anos' },
  ]);
  const [yearMonthsMap, setYearMonthsMap] = useState<Record<string, number[]>>({});
  const [loadingYears, setLoadingYears] = useState(false);

  // Get available months for selected year
  const availableMonths = React.useMemo(() => {
    const options = [{ value: 'all', label: 'Todos os meses' }];

    if (year === 'all') {
      // Show all months when "all years" is selected
      for (let m = 1; m <= 12; m++) {
        options.push({ value: m.toString(), label: ALL_MONTH_LABELS[m] });
      }
    } else {
      // Show only months with data for selected year
      const months = yearMonthsMap[year] || [];
      months.sort((a, b) => a - b).forEach((m) => {
        options.push({ value: m.toString(), label: ALL_MONTH_LABELS[m] });
      });
    }

    return options;
  }, [year, yearMonthsMap]);

  // Reset month when year changes
  const handleYearChange = (newYear: string) => {
    setYear(newYear);
    setMonth('all');
  };

  // Fetch available years when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvailableYears();
    }
  }, [isOpen]);

  const fetchAvailableYears = async () => {
    setLoadingYears(true);
    try {
      // Try to get years with months from dedicated endpoint
      const response = await api.get<{ years: Record<string, number[]> }>('/api/transactions/years');
      const yearsData = response.data.years || {};

      // Store the year-months map
      setYearMonthsMap(yearsData);

      // Build year options
      const yearOptions = [{ value: 'all', label: 'Todos os anos' }];
      Object.keys(yearsData)
        .map(Number)
        .sort((a, b) => b - a)
        .forEach((y) => {
          yearOptions.push({ value: y.toString(), label: y.toString() });
        });

      setAvailableYears(yearOptions);
    } catch (err) {
      // Fallback: generate last 5 years with all months if endpoint doesn't exist
      const currentYear = new Date().getFullYear();
      const fallbackYears = [{ value: 'all', label: 'Todos os anos' }];
      const fallbackMap: Record<string, number[]> = {};

      for (let y = currentYear; y >= currentYear - 5; y--) {
        fallbackYears.push({ value: y.toString(), label: y.toString() });
        fallbackMap[y.toString()] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
      }

      setAvailableYears(fallbackYears);
      setYearMonthsMap(fallbackMap);
    } finally {
      setLoadingYears(false);
    }
  };

  const handleExport = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query params
      const params: Record<string, string> = {
        limit: '1000', // Get up to 1000 records for export
      };

      if (year !== 'all') {
        if (month !== 'all') {
          const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
          const endDate = new Date(parseInt(year), parseInt(month), 0);
          params.startDate = startDate.toISOString();
          params.endDate = endDate.toISOString();
        } else {
          params.startDate = new Date(parseInt(year), 0, 1).toISOString();
          params.endDate = new Date(parseInt(year), 11, 31).toISOString();
        }
      }

      if (exportType === 'transactions') {
        if (transactionType !== 'all') {
          params.type = transactionType;
        }
        if (taxDeductibleOnly) {
          params.taxDeductible = 'true';
        }
      }

      if (exportType === 'receipts' && year !== 'all') {
        params.year = year;
      }

      // Fetch data
      const endpoint = exportType === 'transactions' ? '/api/transactions' : '/api/receipts';
      const response = await api.get(endpoint, { params });
      const data = response.data[exportType] || [];

      if (data.length === 0) {
        setError('Nenhum dado encontrado para exportar com os filtros selecionados.');
        setIsLoading(false);
        return;
      }

      // Generate file
      const date = new Date().toISOString().split('T')[0];
      const filename = `keepnota_${exportType}_${date}`;

      if (format === 'csv') {
        const csv = convertToCSV(data, exportType);
        downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8');
      } else {
        const json = JSON.stringify(data, null, 2);
        downloadFile(json, `${filename}.json`, 'application/json');
      }

      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError('Erro ao exportar dados. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Exportar Dados" size="md">
      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Formato do arquivo
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setFormat('csv')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                format === 'csv'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-secondary-700 hover:border-gray-300'
              }`}
            >
              <FileText
                className={`w-6 h-6 ${format === 'csv' ? 'text-primary-500' : 'text-gray-400'}`}
              />
              <div className="text-left">
                <p className={`font-medium ${format === 'csv' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                  CSV
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Excel, Sheets</p>
              </div>
            </button>

            <button
              onClick={() => setFormat('json')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                format === 'json'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-secondary-700 hover:border-gray-300'
              }`}
            >
              <FileJson
                className={`w-6 h-6 ${format === 'json' ? 'text-primary-500' : 'text-gray-400'}`}
              />
              <div className="text-left">
                <p className={`font-medium ${format === 'json' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                  JSON
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Desenvolvedores</p>
              </div>
            </button>
          </div>
        </div>

        {/* Export Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tipo de dados
          </label>
          <Select
            value={exportType}
            onChange={(e) => setExportType(e.target.value as ExportType)}
            options={typeOptions}
          />
        </div>

        {/* Filters */}
        <div className="space-y-4 p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <Filter className="w-4 h-4" />
            Filtros
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Year */}
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Ano</label>
              {loadingYears ? (
                <div className="flex items-center gap-2 h-10 px-3 border border-gray-300 dark:border-secondary-600 rounded-lg">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-400">Carregando...</span>
                </div>
              ) : (
                <Select
                  value={year}
                  onChange={(e) => handleYearChange(e.target.value)}
                  options={availableYears}
                />
              )}
            </div>

            {/* Month */}
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Mes</label>
              <Select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                options={availableMonths}
                disabled={year === 'all'}
              />
            </div>
          </div>

          {/* Transaction specific filters */}
          {exportType === 'transactions' && (
            <>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Tipo de transacao
                </label>
                <Select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  options={transactionTypeOptions}
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={taxDeductibleOnly}
                  onChange={(e) => setTaxDeductibleOnly(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Apenas dedutiveis de imposto
                </span>
              </label>
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onClose} className="flex-1">
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            isLoading={isLoading}
            leftIcon={<Download className="w-4 h-4" />}
            className="flex-1"
          >
            Exportar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
